<script setup lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { useAppState } from '../store/state';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { Dropdown, LoginIcon, LogoutIcon, BatteryIndicator } from '@wision/ui';
import InformationModal from './InformationModal.vue';
import InfoIcon from './icons/InfoIcon.vue';
import WaterTemplate from './templates/Water.vue';
import RelayTemplate from './templates/Relay.vue';
import MagnetTemplate from './templates/Magnet.vue';
import LevelTemplate from './templates/Level.vue';
import RadiatorTemplate from './templates/Radiator.vue';
import DefaultTemplate from './templates/Default.vue';
import WellTemplate from './templates/Well.vue';
import LeakTemplate from './templates/Leak.vue';
import IndoorClimateTemplate from './templates/IndoorClimate.vue';
import type { Tag, ViewTemplate } from '@nfc-api/tag';
import Navbar from './Navbar.vue';
import { ref, onMounted, onUnmounted } from 'vue';
import { Vue3Lottie } from 'vue3-lottie';
import SignalData from '../assets/lottie/rotating-signal.json';
import { toast } from 'vue-sonner';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const SENSOR_UPDATE_INTERVAL = 5 * 60 * 1000; // Update every five minutes

const state = useAppState();

const infoIsActive = ref<boolean>(false);

const props = defineProps({
  tag: {
    type: Object as PropType<Tag>,
    required: true,
  },
});

if (!props.tag) throw new Error('No tag');

await state.fetchDeviceTag(props.tag.uuid);

let timerId: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  timerId = setInterval(() => {
    if (state.tag)
      void state.fetchDeviceTag(props.tag.uuid);
  }, SENSOR_UPDATE_INTERVAL);
});

onUnmounted(() => {
  if (timerId !== null) clearInterval(timerId);
});

const template = state.viewValues.template;

const mapTemplate: Record<ViewTemplate, { templateComponent: ReturnType<typeof defineComponent>, templateName: string }> = {
  default: { templateComponent: DefaultTemplate, templateName: 'common.temp' },
  water: { templateComponent: WaterTemplate, templateName: 'nfc.template.water' },
  magnet: { templateComponent: MagnetTemplate, templateName: 'nfc.template.magnet' },
  relay: { templateComponent: RelayTemplate, templateName: 'nfc.template.relay' },
  radiator: { templateComponent: RadiatorTemplate, templateName: 'nfc.template.radiator' },
  leak: { templateComponent: LeakTemplate, templateName: 'nfc.template.leak' },
  level: { templateComponent: LevelTemplate, templateName: 'nfc.template.level' },
  indoorClimate: { templateComponent: IndoorClimateTemplate, templateName: 'nfc.template.indoorClimate' },
  well: { templateComponent: WellTemplate, templateName: 'nfc.template.well' },
};

const { templateComponent, templateName } = mapTemplate[template];

const { viewValues } = storeToRefs(useAppState());

const router = useRouter();

const loginHandler = () => router.push({ name: 'userlogin' });

const logoutHandler = () => {
  state.logout(`/onboard/${props.tag.uuid}/login`);
  toast.info(t('toast.sessionExpired'));
};
</script>

<template>
  <InformationModal class="pt-10" v-if="infoIsActive" @close="infoIsActive = false" />
  <div v-else class="w-full flex flex-col items-center">
    <Navbar class="topBar flex absolute top-0 left-0 p-4 z-10">
      <template #left>
        <BatteryIndicator :value="viewValues.battery" :unit="viewValues.batteryUnit" />
      </template>
      <template #center><span class="uppercase">{{ `WISION ${$t(templateName)} ${$t('common.device')}` }}</span></template>
      <template #right>
        <Dropdown customStyles="left-0">
          <template v-slot:button>
            <svg class="rotate-90" width="25" height="25" viewBox="0 0 55 55" version="1.1" xmlns="http://www.w3.org/2000/svg"
                 xmlns:xlink="http://www.w3.org/1999/xlink">
              <g opacity="1.0" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <g transform="translate(-302.000000, -408.000000)">
                  <g transform="translate(302.400000, 408.121287)">
                    <polygon points="0 0 54 0 54 53.9851485 0 53.9851485"></polygon>
                    <path
                      d="M13.5,22.4938119 C11.025,22.4938119 9,24.518255 9,26.9925743 C9,29.4668936 11.025,31.4913366 13.5,31.4913366 C15.975,31.4913366 18,29.4668936 18,26.9925743 C18,24.518255 15.975,22.4938119 13.5,22.4938119 Z M40.5,22.4938119 C38.025,22.4938119 36,24.518255 36,26.9925743 C36,29.4668936 38.025,31.4913366 40.5,31.4913366 C42.975,31.4913366 45,29.4668936 45,26.9925743 C45,24.518255 42.975,22.4938119 40.5,22.4938119 Z M27,22.4938119 C24.525,22.4938119 22.5,24.518255 22.5,26.9925743 C22.5,29.4668936 24.525,31.4913366 27,31.4913366 C29.475,31.4913366 31.5,29.4668936 31.5,26.9925743 C31.5,24.518255 29.475,22.4938119 27,22.4938119 Z"
                      fill="#FFFFFF" fill-rule="nonzero"></path>
                  </g>
                </g>
              </g>
            </svg>
          </template>

          <template v-slot:default="dropdownContent">
            <div class="flex flex-col justify-center w-32 p-2 bg-button-panel gap-1">
              <div
                v-if="!state.hasAccess"
                @click="loginHandler"
                class="flex flex-row z-10 items-center justify-start"
              >
                <LoginIcon />
                <span class="text-base text-dark-primary px-5">{{ $t('nfc.form.login') }}</span>
              </div>

              <div
                v-else
                @click="logoutHandler(); dropdownContent.close()"
                class="flex flex-row z-10 items-center justify-start"
              >
                <LogoutIcon />
                <span class="text-base text-dark-primary px-5">{{ $t('nav.logout') }}</span>
              </div>
            </div>
          </template>
        </Dropdown>
      </template>
    </Navbar>

    <div v-if="viewValues?.values?.length! > 0" class="max-w-[1000px] w-full">
      <component v-bind:is="templateComponent" :viewValues="viewValues" />
    </div>
    <div v-else>
      <span class="animate-pulse">
        <Vue3Lottie :animationData="SignalData" :height="100" :width="100" :loop="true" />
        {{ $t('nfc.device.waiting') }}...
      </span>
    </div>
    <Navbar class="footer flex absolute bottom-5 left-0 p-4">
      <template #left>
        <svg width="30" height="25" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <g opacity="0.279250372" stroke="#FFF" fill="none" fill-rule="evenodd">
            <rect x=".5" y=".5" width="7" height="7" rx="1" />
            <rect x=".5" y="11.5" width="7" height="7" rx="1" />
            <rect x="11.5" y=".5" width="7" height="7" rx="1" />
            <rect x="11.5" y="11.5" width="7" height="7" rx="1" />
          </g>
        </svg>
      </template>
      <template #center>
        <div class="flex flex-col bottom-5 text-center visible md:visible vlg:visible ">
          <svg class="fill-current mx-auto" width="40" height="40" viewBox="0 0 27 24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.426 7.32H7.899l5.268 9.407 5.259-9.407M6.38 2.838c5.547-.027 11.092.029 16.637-.046 1.17-.017 1.556.544 1.9 1.637H6.468c.152 1.014.649 1.263 1.486 1.258 5.584-.029 11.17-.017 16.754-.012 1.537 0 2.123 1.05 1.36 2.416-2.748 4.92-5.525 9.823-8.22 14.772-.57 1.047-1.22 1.316-2.334.941 3.065-5.49 6.114-10.945 9.177-16.431-.935-.387-1.37-.027-1.78.714-2.724 4.934-5.513 9.827-8.22 14.771-.536.982-1.134 1.348-2.303.915 3.056-5.474 6.095-10.924 9.147-16.393-.975-.413-1.392.022-1.79.74-2.712 4.894-5.454 9.77-8.187 14.651-.342.612-.555 1.217-1.505 1.229-.998.015-1.24-.617-1.603-1.27C5.778 17.953 3.141 13.152.4 8.418c-.623-1.074-.51-1.812.296-2.71 3.084 5.506 6.144 10.97 9.224 16.473.764-.59.755-1.132.328-1.887-2.782-4.9-5.493-9.84-8.294-14.728-.57-.996-.588-1.751.335-2.634l9.227 16.47c.74-.653.717-1.207.29-1.962-2.824-4.966-5.6-9.96-8.39-14.948-.754-1.35-.152-2.46 1.357-2.463C10.32.022 15.865.061 21.41 0c1.123-.012 1.634.446 1.903 1.604H4.911c.108 1.055.654 1.239 1.47 1.234"
              fill-rule="evenodd" />
          </svg>
          <span class="text-xl mt-3 hidden md:block lg:block xl:block">Powered by WISION</span>
        </div>
      </template>
      <template #right>
        <InfoIcon @click="infoIsActive = true" />
      </template>
    </Navbar>
  </div>
</template>

